import { useState } from 'react'

import { useTranslation } from 'react-i18next'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Button } from '@/components/ui'
import { IdentificadorClienteAtendimento } from '@/features/atendimento/shared/components/identificador-cliente-atendimento'
import type { TAtendimento } from '@/types/atendimento'
import { getEstadoCivilKeyMap } from '@/utils/key-map'

type Props = {
  cliente: Extract<TAtendimento['cliente'], { $type: 'Brasil' }>
}

const ClienteInfo = ({ label, value }: { label: string; value: string | null }) => {
  const { t } = useTranslation('atendimento')
  return (
    <div aria-label={label}>
      <span className="font-medium">{label}:</span> {value ?? t('components.atendimento_info.nao_informado')}
    </div>
  )
}

const DetalhesClienteAtendimento = ({ cliente }: Props) => {
  const { t } = useTranslation('atendimento')
  const [showMore, setShowMore] = useState(false)
  const estadoCivilKeyMap = getEstadoCivilKeyMap()

  const truncateWithEllipsis = (text: string, length: number) => {
    if (text.length <= length) return text
    return text.substring(0, length) + '...'
  }

  return (
    <div className="w-80 space-y-4 px-3 py-2">
      <div className="flex items-center gap-2">
        <IdentificadorClienteAtendimento nome={cliente.nome} />
      </div>
      <div className="font-nunito space-y-2 text-sm text-neutral-800">
        <ClienteInfo label={t('components.atendimento_info.chaves.cpf')} value={cliente.cpf} />
        <ClienteInfo label={t('components.atendimento_info.chaves.telefone')} value={cliente.telefone} />
        <ClienteInfo label={t('components.atendimento_info.chaves.profissao')} value={cliente.profissao} />
        <ClienteInfo
          label={t('components.atendimento_info.chaves.estado_civil')}
          value={cliente.estadoCivil ? estadoCivilKeyMap[cliente.estadoCivil] : null}
        />
        <ClienteInfo
          label={t('components.atendimento_info.chaves.filhos')}
          value={cliente.filhos ? t('components.atendimento_info.sim') : t('components.atendimento_info.nao')}
        />
        <ClienteInfo
          label={t('components.atendimento_info.chaves.proprietario')}
          value={cliente.proprietario ? t('components.atendimento_info.sim') : t('components.atendimento_info.nao')}
        />
        <div className="space-y-2">
          <div>
            <span className="font-medium">{t('components.atendimento_info.chaves.resumo')}: </span>
            <Render.If
              condition={cliente.resumo}
              fallback={<span className="text-sm">{t('components.atendimento_info.sem_comentarios')}</span>}
            >
              {(resumo) => (
                <>
                  <span className="text-sm leading-relaxed wrap-break-word">
                    {showMore ? resumo : truncateWithEllipsis(resumo, 52)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="z-50 mt-2 flex h-6 pl-0 font-medium text-sky-700"
                    onClick={() => setShowMore(!showMore)}
                  >
                    <Icon
                      name="chevron-down"
                      className={`mr-1 h-5 w-5 transition-transform ${showMore ? 'rotate-180' : ''}`}
                    />
                    {showMore ? t('components.atendimento_info.ver_menos') : t('components.atendimento_info.ver_mais')}
                  </Button>
                </>
              )}
            </Render.If>
          </div>
        </div>
      </div>
    </div>
  )
}

export { DetalhesClienteAtendimento }
